import _ from 'lodash'
import { assign, createMachine, fromPromise } from 'xstate'

import Api from '../../../api/_legacy'
import { BusinessUnit, Client, Strategy, User } from '../types'
import { mapToClient } from './mappers'

const TENANT_ID = 'absa'

export type CurrentUserType = 'client' | 'admin'

export type ClientsState = {
  availableFeeTypes: string[]
  client?: Client
  clientId?: string
  clientName?: string
  clientUsers?: User[]
  clients: Client[]
  counterpartyIds: string[]
  depositInstruments: string[]
  error?: any
  filteredClients: Client[]
  initialActiveTab: 'details' | 'users' | 'add-business-unit'
  isBusinessUnitSavedSuccessfully: boolean
  isClientSavedSuccessfully: boolean
  loanInstruments: string[]
  selectedBusinessUnit?: BusinessUnit
  selectedClient?: Client
  selectedClientUser?: User
  strategies: Strategy[]
}

type SelectClientEvent = { clientId: string; type: 'SELECT_CLIENT' }
type FetchClientsEvent = { tenant: string; type: 'FETCH_CLIENTS' }
type FetchStrategiesEvent = { tenant: string; type: 'FETCH_STRATEGIES' }
type FetchDepositInstrumentsEvent = { tenant: string; type: 'FETCH_DEPOSITS' }
type FetchLoanInstrumentsEvent = { tenant: string; type: 'FETCH_LOANS' }
type FetchCptyIdsEvent = { tenant: string; type: 'FETCH_CPTY_IDS' }
type SelectBusinessUnitEvent = {
  businessUnitId: string
  clientId: string
  type: 'SELECT_BUSINESS_UNIT'
}

type SearchClientsEvent = {
  searchTerm: string
  type: 'SEARCH_CLIENTS'
}

type AddClientCompleteEvent = { type: 'ADD_CLIENT_COMPLETE' }
type NewBusinessUnitSaved = { type: 'NEW_BUSINESS_UNIT_SAVED' }
type ExistingBusinessUnitSaved = { type: 'EXISTING_BUSINESS_UNIT_SAVED' }
type ClientSaved = { type: 'CLIENT_SAVED' }
type ResetSavedIndicator = { type: 'RESET_SAVED_INDICATOR' }

type ClientsEvent =
  | SelectClientEvent
  | FetchClientsEvent
  | FetchStrategiesEvent
  | FetchDepositInstrumentsEvent
  | FetchLoanInstrumentsEvent
  | FetchCptyIdsEvent
  | SelectBusinessUnitEvent
  | SearchClientsEvent
  | AddClientCompleteEvent
  | ClientSaved
  | NewBusinessUnitSaved
  | ExistingBusinessUnitSaved
  | ResetSavedIndicator

export const ClientsMachine = createMachine(
  {
    types: {} as {
      context: ClientsState
      events: ClientsEvent
    },
    id: 'clients-machine',
    initial: 'idle',
    context: {
      filteredClients: [],
      clients: [],
      initialActiveTab: 'details',
      isBusinessUnitSavedSuccessfully: false,
      isClientSavedSuccessfully: false,
      strategies: [],
      depositInstruments: [],
      loanInstruments: [],
      counterpartyIds: [],
      availableFeeTypes: []
    },
    states: {
      idle: {
        on: {
          FETCH_CLIENTS: {
            target: 'strategiesLoading'
          }
        }
      },
      clientSelected: {
        initial: 'clientDetails',
        states: {
          clientDetails: {
            initial: 'untouched',
            states: {
              untouched: {}
            }
          }
        },
        on: {
          ADD_CLIENT_COMPLETE: {
            target: 'loading'
          },
          SELECT_BUSINESS_UNIT: {
            target: 'businessUnitSelected',
            actions: ['selectBusinessUnit']
          },
          SEARCH_CLIENTS: {
            actions: ['filterClients'],
            target: 'clientsLoaded'
          },
          SELECT_CLIENT: {
            target: 'clientSelected.clientDetails',
            actions: ['selectClient']
          },
          NEW_BUSINESS_UNIT_SAVED: {
            target: 'reload',
            actions: ['setAddBusinessUnitTab', 'setBusinessUnitSaveSuccessful']
          },
          CLIENT_SAVED: {
            target: 'reload',
            actions: ['setClientDetailsTab', 'setClientSaveSuccessful']
          },
          RESET_SAVED_INDICATOR: {
            actions: ['resetSavedIndicator']
          }
        }
      },
      strategiesLoading: {
        invoke: [
          {
            id: 'getStrategies',
            src: 'fetchStrategies',
            onDone: {
              actions: ['setStrategies'],
              target: 'depositInstrumentsLoading'
            }
          }
        ]
      },
      depositInstrumentsLoading: {
        invoke: [
          {
            id: 'getDepositInstruments',
            src: 'fetchDepositInstruments',
            onDone: {
              actions: ['setDepositInstruments'],
              target: 'loanInstrumentsLoading'
            }
          }
        ]
      },
      loanInstrumentsLoading: {
        invoke: [
          {
            id: 'getLoanInstruments',
            src: 'fetchLoanInstruments',
            onDone: {
              actions: ['setLoanInstruments'],
              target: 'cptyIdsLoading'
            }
          }
        ]
      },
      cptyIdsLoading: {
        invoke: [
          {
            id: 'getCptyIds',
            src: 'fetchCptyIds',
            onDone: {
              actions: ['setCptyIds'],
              target: 'feeTypesLoading'
            }
          }
        ]
      },
      feeTypesLoading: {
        invoke: [
          {
            id: 'getFeeTypes',
            src: 'fetchFeeTypes',
            onDone: {
              actions: ['setFeeTypes'],
              target: 'loading'
            }
          }
        ]
      },
      loading: {
        invoke: [
          {
            id: 'getClients',
            src: 'fetchClients',
            onDone: {
              target: 'clientsLoaded',
              actions: ['setClients']
            },
            onError: {
              target: 'failure',
              actions: assign({ error: ({ event }) => event.error })
            }
          }
        ]
      },
      reload: {
        invoke: {
          id: 'getClients',
          src: 'fetchClients',
          input: ({ context, event }) => ({ context, event }),
          onDone: {
            target: 'clientSelected',
            actions: ['setClients', 'reselectPreviouslySelectedClient']
          },
          onError: {
            target: 'failure',
            actions: assign({ error: ({ event }) => event.error })
          }
        }
      },
      reloadAfterBusinessUnitSave: {
        invoke: {
          id: 'getClients',
          src: 'fetchClients',
          input: ({ context, event }) => ({ context, event }),
          onDone: {
            target: 'businessUnitSelected',
            actions: ['setClients', 'reselectPreviouslySelectedBusinessUnit']
          },
          onError: {
            target: 'failure',
            actions: assign({ error: ({ event }) => event.error })
          }
        }
      },
      clientsLoaded: {
        on: {
          SELECT_CLIENT: {
            target: 'clientSelected',
            actions: ['selectClient']
          },
          SEARCH_CLIENTS: {
            actions: ['filterClients'],
            target: 'clientsLoaded'
          },
          ADD_CLIENT_COMPLETE: {
            target: 'loading'
          }
        }
      },
      businessUnitSelected: {
        on: {
          ADD_CLIENT_COMPLETE: {
            target: 'loading'
          },
          SELECT_BUSINESS_UNIT: {
            target: 'businessUnitSelected',
            actions: ['selectBusinessUnit']
          },
          SELECT_CLIENT: {
            target: 'clientSelected',
            actions: ['selectClient']
          },
          EXISTING_BUSINESS_UNIT_SAVED: {
            target: 'reloadAfterBusinessUnitSave',
            actions: ['setBusinessUnitSaveSuccessful', 'reselectPreviouslySelectedBusinessUnit']
          },
          RESET_SAVED_INDICATOR: {
            actions: ['resetSavedIndicator']
          }
        }
      },
      failure: {}
    }
  },
  {
    actors: {
      fetchClients: fromPromise(async () => {
        const response = await Api.usermanager.currentList()
        const data = response.data.clients

        const formatted = data.map(mapToClient)

        return formatted
      }),
      fetchStrategies: fromPromise(async () => {
        const response = await Api.tenant.strategyDetail(TENANT_ID)
        const { data } = response

        const formatted = data.map(x => ({
          id: x.strategyTemplateId,
          name: x.strategyName,
          sortOrder: x.sortOrder || 0
        }))

        const sorted = _.orderBy(formatted, 'sortOrder', 'asc')

        return sorted
      }),
      fetchDepositInstruments: fromPromise(async () => {
        const response = await Api.tenant.cashinstrumentDetail(TENANT_ID)
        const { data } = response

        const filtered = data.filter(x => x.type === 'Deposit')
        const result = filtered.map(x => x.instrumentId)

        return result
      }),
      fetchLoanInstruments: fromPromise(async () => {
        const response = await Api.tenant.cashinstrumentDetail(TENANT_ID)
        const { data } = response

        const filtered = data.filter(x => x.type === 'Loan')
        const result = filtered.map(x => x.instrumentId)

        return result
      }),
      fetchCptyIds: fromPromise(async () => {
        const response = await Api.tenant.counterpartyidsDetail(TENANT_ID)
        return response.data
      }),
      fetchFeeTypes: fromPromise(async () => {
        const response = await Api.tenant.feetypeDetail(TENANT_ID)
        return response.data
      })
    },
    actions: {
      setClients: assign(({ context, event }: any) => ({
        ...context,
        clients: event.output,
        filteredClients: event.output
      })),
      setStrategies: assign(({ context, event }: any) => ({
        ...context,
        strategies: event.output
      })),
      setDepositInstruments: assign(({ context, event }: any) => ({
        ...context,
        depositInstruments: event.output
      })),
      setLoanInstruments: assign(({ context, event }: any) => ({
        ...context,
        loanInstruments: event.output
      })),
      setCptyIds: assign(({ context, event }: any) => ({
        ...context,
        counterpartyIds: event.output
      })),
      setFeeTypes: assign(({ context, event }: any) => ({
        ...context,
        availableFeeTypes: event.output
      })),
      selectClient: assign(({ context, event }: any) => {
        const newClients = [...context.filteredClients]
        const index = newClients.findIndex(x => x.clientId === event.clientId)

        const client = { ...newClients[index] }
        client.isExpanded = true

        newClients.filter((_, i) => i !== index).forEach(x => (x.isExpanded = false))

        newClients[index] = client
        const clientDisallowedStrategies = client.disallowedStrategies
          ? [...client.disallowedStrategies]
          : []

        return {
          ...context,
          filteredClients: newClients,
          selectedClient: {
            ...client,
            disallowedStrategies: clientDisallowedStrategies
          },
          client,
          selectedBusinessUnit: undefined
        }
      }),
      selectBusinessUnit: assign(({ context, event }: any) => {
        // find business unit to select, and its client name
        let businessUnit
        let clientName = ''
        let clientId = ''
        let client
        for (const c of context.clients) {
          for (const bu of c.businessUnits) {
            if (bu.businessUnitId === event.businessUnitId) {
              businessUnit = bu
              break
            }
          }
          if (businessUnit) {
            client = c
            // eslint-disable-next-line prefer-destructuring
            clientName = c.clientName
            // eslint-disable-next-line prefer-destructuring
            clientId = c.clientId
            break
          }
        }

        return {
          ...context,
          client,
          selectedClient: undefined,
          selectedBusinessUnit: businessUnit,
          clientName,
          clientId,
          isBusinessUnitSavedSuccessfully: false,
          isClientSavedSuccessfully: false
        }
      }),
      reselectPreviouslySelectedClient: assign(({ context }) => {
        if (!context.selectedClient) {
          return context
        }

        const newClients = [...context.filteredClients]
        const index = newClients.findIndex(x => x.clientId === context.selectedClient.clientId)
        const client = { ...newClients[index] }
        client.isExpanded = true
        newClients[index] = client
        const clientDisallowedStrategies = client.disallowedStrategies
          ? [...client.disallowedStrategies]
          : []

        return {
          ...context,
          filteredClients: newClients,
          client,
          selectedClient: {
            ...client,
            disallowedStrategies: clientDisallowedStrategies
          }
        }
      }),
      reselectPreviouslySelectedBusinessUnit: assign(({ context }) => {
        if (!context.selectedBusinessUnit) {
          return {}
        }

        let businessUnit
        let clientName = ''
        let clientId = ''
        let client
        for (const c of context.clients) {
          for (const bu of c.businessUnits) {
            if (bu.businessUnitId === context.selectedBusinessUnit.businessUnitId) {
              businessUnit = bu
              break
            }
          }
          if (businessUnit) {
            c.isExpanded = true
            // eslint-disable-next-line prefer-destructuring
            clientName = c.clientName
            // eslint-disable-next-line prefer-destructuring
            clientId = c.clientId
            client = c
            break
          }
        }

        return {
          client,
          selectedClient: undefined,
          selectedBusinessUnit: businessUnit,
          clientName,
          clientId
        }
      }),
      setBusinessUnitSaveSuccessful: assign(() => ({
        isBusinessUnitSavedSuccessfully: true
      })),
      setClientSaveSuccessful: assign(() => ({
        isClientSavedSuccessfully: true
      })),
      resetSavedIndicator: assign(() => ({
        isClientSavedSuccessfully: false,
        isBusinessUnitSavedSuccessfully: false
      })),
      filterClients: assign(({ context, event }: any) => ({
        ...context,
        filteredClients: context.clients.filter(x =>
          x.clientName.toLowerCase().includes(event.searchTerm.toLowerCase())
        )
      })),
      setClientDetailsTab: assign(({ context }) => ({
        ...context,
        initialActiveTab: 'details'
      })),
      setAddBusinessUnitTab: assign(({ context }) => ({
        ...context,
        initialActiveTab: 'add-business-unit'
      }))
    }
  }
)
